export * from './SSE';
import { BackendConfig, requests as baseRequests, ExecutionContextProvider, ReadmodelChange } from './SSE';

export type RealtimeSessionMessage<T = any> = {
  type: string;
  ref: string;
  data: T;
};

const subscriptions: Map<string, (( message: any )=>void )[]> = new Map();
// Persistent registry of every active subscription, keyed by a per-call id, so
// each can be re-issued to the server after a WebSocket reconnect. The previous
// `initialSubscriptions` buffer only held subscriptions made BEFORE the first
// connect and was drained on open, so it could not restore subscriptions across
// a reconnect — any WS drop (backend restart, laptop sleep, network blip) left
// every subscription dead (signals stop arriving) until a full page reload.
const activeSubscriptions: Map<string, { path: string, params: Record<string, any>, name: string, extraHeaders?: Record<string, string> }> = new Map();
let subscriptionSeq = 0;
let websocket: WebSocket | null = null;
let reconnectAttempts = 0;
const RECONNECT_BASE_DELAY = 1000; // 1s
let isConnected = false;
// Set when the consumer explicitly closes the session so the socket's `onclose`
// does not schedule a reconnect (which would otherwise loop forever on signout).
let manualClose = false;
let onOpenSubscriptions: () => void;

const onMessage = <T>( type: string, ref: string, onChange: ( message: T ) => void ) => {
  const refName = `${type}-${ref}`;
  const currentSubscriptions = subscriptions.get( refName ) || [];
  subscriptions.set( refName, [ ...currentSubscriptions, onChange ]);
};

const offMessage = <T>( type: string, ref: string, onChange: ( message: T ) => void ) => {
  const refName = `${type}-${ref}`;
  const currentSubscriptions = subscriptions.get( refName ) || [];
  subscriptions.set( refName, currentSubscriptions.filter(( s ) => s !== onChange ));
};

export type RealtimeSession = {
  onMessage: <T>( type: string, ref: string, onChange: ( message: T ) => void ) => void;
  offMessage: <T>( type: string, ref: string, onChange: ( message: T ) => void ) => void;
  close: () => void;
};

export const getCustomExports = () => {
  function initializeRealtimeSession( wsUrl: string ): RealtimeSession {
    function connect() {
      websocket = new WebSocket( wsUrl );

      websocket.onopen = () => {
        reconnectAttempts = 0;
        isConnected = true;
        // Re-issue ALL active subscriptions on every open — the first connect
        // AND every reconnect. The onChange handlers in `subscriptions` are
        // preserved across the drop (they are keyed by subscription name, which
        // the re-issued GETs reuse), so live updates resume automatically.
        onOpenSubscriptions();
        console.log( `Connection with the server established for Realtime Session` );
      };

      websocket.onmessage = ( e ) => {
        const message = JSON.parse( e.data.toString());
        ( subscriptions.get( `${message.type}-${message.ref}` ) || []).forEach(( s ) => s( message.data ));
        ( subscriptions.get( `${message.type}-*` ) || []).forEach(( s ) => s( message.data ));
      };

      websocket.onclose = () => {
        isConnected = false;
        // Do NOT clear `subscriptions` here: the consumer components are still
        // mounted and their handlers are still valid. Keeping them means the
        // reconnect's re-issued subscriptions deliver to the existing handlers.
        if ( !manualClose ) scheduleReconnect();
        console.log( `Connection with the server closed for Realtime Session` );
      };

      websocket.onerror = () => {
        console.warn( `Connection with the server errored for Realtime Session, closing connection` );
        websocket?.close();
      };
    }

    function scheduleReconnect() {
      const delay = Math.min( RECONNECT_BASE_DELAY * 2 ** reconnectAttempts, 30000 );
      setTimeout(() => {
        reconnectAttempts++;
        connect();
      }, delay );
    }

    // A fresh initialize (e.g. sign-in after a prior signout) re-enables
    // auto-reconnect that an explicit close() had disabled.
    manualClose = false;
    connect();

    return {
      onMessage,
      offMessage,
      close: () => {
        manualClose = true;
        websocket?.close();
      }
    };
  }

  return {
    initializeRealtimeSession
  };
};

/**
 * A subscription's initial GET is issued fire-and-forget: the payload comes
 * back over the WebSocket (`manager.send`), not in the HTTP response, so there
 * is no caller waiting on this promise and no natural place to `await` it.
 *
 * But an unawaited rejection is not free. `baseRequests().get` rejects with the
 * raw `Response` object on a non-2xx, so a failing subscription GET surfaces as
 * `Uncaught (in promise) Response` in the console — and any consumer that
 * installs a global `unhandledrejection` handler (a common pattern for routing
 * errors into a toast/notification surface) will report it to the user. That
 * fires for entirely expected transients: during a first-time-signup
 * provisioning window, every account-scoped subscription GET fails until the
 * session is bound to an account, so a new user gets a burst of error toasts
 * for a race that resolves itself a second later.
 *
 * Swallowing to a `console.warn` is the right level here. The subscription
 * itself is already resilient — it stays in `activeSubscriptions` and is
 * re-issued on the next `onopen`, and callers that need a guaranteed initial
 * payload use the query-first + subscribe pattern, where the QUERY is the
 * awaited call that surfaces errors properly.
 *
 * NOTE: this file is COPIED VERBATIM into each consumer's client library on
 * every `pv3 g cl` run (see `copyBaseFile` in `openApiLibrary.ts`), overwriting
 * whatever is there. Fixes must live HERE, in the template — a patch applied
 * downstream is silently reverted by the next regeneration.
 */
const issueSubscriptionRequest = (
  request: Promise<unknown>,
  name: string
): void => {
  void Promise.resolve( request ).catch(( error: unknown ) => {
    const status = error instanceof Response ? ` (HTTP ${error.status})` : '';
    console.warn( `Subscription request for ${name} failed${status}; will be re-issued on reconnect`, error );
  });
};

export const requests = ( config: BackendConfig, _fetch = fetch, headers: Record<string, string | undefined> = {}, contextProvider: ExecutionContextProvider ) => {
  onOpenSubscriptions = () => {
    // Re-issue every active subscription from the persistent registry. NOT
    // drained — the same set must be replayed on the next reconnect too.
    activeSubscriptions.forEach(( s ) => {
      console.log( `Subscribing to Readmodel changes for ${s.name}...` );
      issueSubscriptionRequest(
        baseRequests( config, _fetch, headers, contextProvider ).get( s.path, s.params, { 'X-Realtime-Session-Subscription': s.name, ...( s.extraHeaders || {}) }),
        s.name
      );
    });
  };

  return {
    ...baseRequests( config, _fetch, headers, contextProvider ),
    // This only supports cookies for authentication, not tokens
    subscribe: ( path: string, params: Record<string, any> = {}, name: string, onChange: ( change: ReadmodelChange )=>void ) => {
      // Extract subscription options from params
      const { _snapshot, _changes, ...queryParams } = params;
      const extraHeaders: Record<string, string> = {};
      if ( _snapshot === false ) extraHeaders['X-Subscription-Snapshot'] = 'false';
      if ( _changes === 'signal' ) extraHeaders['X-Subscription-Changes'] = 'signal';

      // Track every subscription in the persistent registry so it is re-issued
      // on reconnect (see `onOpenSubscriptions`). `subscriptionId` scopes the
      // entry to THIS call so `close()` removes only its own registration.
      const subscriptionId = `${name}-${subscriptionSeq++}`;
      activeSubscriptions.set( subscriptionId, { path, params: queryParams, name, extraHeaders });

      // Issue the GET now if already connected; otherwise the next `onopen`
      // re-issues it from the registry (covers first connect and reconnect).
      if ( isConnected ) {
        console.log( `Subscribing to Readmodel changes for ${name}...` );
        issueSubscriptionRequest(
          baseRequests( config, _fetch, headers, contextProvider ).get( path, queryParams, { 'X-Realtime-Session-Subscription': name, ...extraHeaders }),
          name
        );
      }
      onMessage( 'realtime-session-subscription', name, onChange );
      return {
        close: () => {
          activeSubscriptions.delete( subscriptionId );
          offMessage( 'realtime-session-subscription', name, onChange );
          if ( isConnected && websocket?.readyState === WebSocket.OPEN ) {
            websocket?.send?.( `CLOSE ${name}` );
          }
        }
      };
    }
  };
};